import { css } from "@emotion/css";
import { FocusableElement } from "@react-types/shared";
import classNames from "classnames";
import { DOMAttributes, InputHTMLAttributes } from "react";

import {
  INPUT_HEIGHT,
  INPUT_WRAPPER_MIN_HEIGHT,
  INPUT_WRAPPER_MIN_WIDTH,
} from "../../constants";
interface ToggleSwitchProps extends InputHTMLAttributes<HTMLInputElement> {
  card?: boolean;
  focusProps?: DOMAttributes<FocusableElement>;
  testID?: string;
}

export function ToggleSwitch(props: ToggleSwitchProps) {
  const { card, focusProps, testID = "toggle-switch", ...rest } = props;
  const toggleWidth = "38px";

  return (
    <div className={styles.inputWrapper} data-testid={testID}>
      <div
        className={css(
          styles.toggle,
          css({
            width: toggleWidth,
          })
        )}
      >
        <input
          className={css(styles.input, {
            "&:checked + span": {
              backgroundColor: "var(--background-selected-strong)",
            },
            "&:disabled + span": {
              backgroundColor: "var(--border-input-disabled)",
            },
            "&:focus:checked + span": css(
              !card
                ? {
                    boxShadow: "var(--focus-action)",
                  }
                : undefined
            ),
          })}
          {...focusProps}
          {...rest}
        />
        <span
          className={classNames(
            "slider",
            css(styles.slider, {
              backgroundColor: "var(--border-input)",
              "&:before": {
                backgroundColor: "var(--icon-inverse)",
              },
            })
          )}
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

const styles = {
  inputWrapper: css({
    position: "relative",
    height: INPUT_WRAPPER_MIN_HEIGHT,
    minWidth: INPUT_WRAPPER_MIN_WIDTH,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  }),
  toggle: css({
    position: "relative",
    height: INPUT_HEIGHT,
  }),
  input: css({
    opacity: 0,
    margin: 0,
    "&:checked + span:before": {
      transform: "translateX(20px)",
    },
    "&:checked + span:hover": {
      backgroundPositionY: "5px",
      backgroundPositionX: "6px",
      backgroundRepeat: "no-repeat",
      backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEwIDgiIGZpbGw9IiNmZmZmZmYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik05LjY3NDIzIDAuMzM0NjM0QzkuODgyODIgMC41NDg5NjQgMTAgMC44Mzk2MiAxMCAxLjE0MjY4QzEwIDEuNDQ1NzUgOS44ODI4MiAxLjczNjQgOS42NzQyMyAxLjk1MDczTDQuMTEwOTUgNy42NjUzN0MzLjkwMjI5IDcuODc5NjMgMy42MTkzMyA4IDMuMzI0MyA4QzMuMDI5MjYgOCAyLjc0NjMgNy44Nzk2MyAyLjUzNzY1IDcuNjY1MzdMMC4zMTIzMzYgNS4zNzk1MUMwLjEwOTY1NiA1LjE2Mzk1IC0wLjAwMjQ5MzA0IDQuODc1MjUgNC4yMDYxNGUtMDUgNC41NzU1OEMwLjAwMjU3NzE2IDQuMjc1OTEgMC4xMTk1OTQgMy45ODkyNCAwLjMyNTg5IDMuNzc3MzRDMC41MzIxODUgMy41NjU0MyAwLjgxMTI1MyAzLjQ0NTIzIDEuMTAyOTkgMy40NDI2MkMxLjM5NDcyIDMuNDQwMDIgMS42NzU3OCAzLjU1NTIyIDEuODg1NjMgMy43NjM0MUwzLjMyNDMgNS4yNDEyMkw4LjEwMDkzIDAuMzM0NjM0QzguMzA5NTkgMC4xMjAzNjggOC41OTI1NCAwIDguODg3NTggMEM5LjE4MjYyIDAgOS40NjU1OCAwLjEyMDM2OCA5LjY3NDIzIDAuMzM0NjM0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==")`,
    },
    "&:hover:not(:checked) + span": {
      backgroundPositionY: "5px",
      backgroundPositionX: "22px",
      backgroundRepeat: "no-repeat",
      backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSIjZmZmZmZmIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNNC4wMTIyNiA1LjU2MzQ4TDEuODk1NjUgNy42NzUzNEMxLjY3Mzg4IDcuODk2NzMgMS40MTQ4IDguMDA0OSAxLjExODQzIDcuOTk5ODNDMC44MjI1NjEgNy45OTQyNiAwLjU2MzQ4NyA3Ljg4MDc3IDAuMzQxMjA1IDcuNjU5MzhDMC4xMTk0MzEgNy40Mzc0OCAwLjAwODU0Mzg3IDcuMTc4ODUgMC4wMDg1NDM4NyA2Ljg4MzQ5QzAuMDA4NTQzODcgNi41ODc2MiAwLjExOTQzMSA2LjMyODk5IDAuMzQxMjA1IDYuMTA3NTlMMi40NDE4MyAzLjk5NTc0TDAuMzI1MjE5IDEuODgzODhDMC4xMDM0NDUgMS42NjI0OCAtMC4wMDQ5MDQ2MiAxLjQwNDExIDAuMDAwMTcwMyAxLjEwODc0QzAuMDA1NzUyNzEgMC44MTI4NzcgMC4xMTk0MzEgMC41NTQyNDUgMC4zNDEyMDUgMC4zMzI4NTFDMC41NTI4MjkgMC4xMTA5NSAwLjgwOTM2NiAwIDEuMTEwODIgMEMxLjQxMjI3IDAgMS42NzM4OCAwLjExMDk1IDEuODk1NjUgMC4zMzI4NTFMMy45OTYyOCAyLjQ0NDcxTDYuMDk2OSAwLjMzMjg1MUM2LjMxODY4IDAuMTEwOTUgNi41ODAyOSAwIDYuODgxNzQgMEM3LjE4MzE5IDAgNy40NDUwNiAwLjExMDk1IDcuNjY3MzQgMC4zMzI4NTFDNy44ODkxMSAwLjU1NDI0NSA4IDAuODE1NDEgOCAxLjExNjM0QzggMS40MTcyOCA3Ljg4OTExIDEuNjc4NDQgNy42NjczNCAxLjg5OTg0TDUuNTUwNzMgNC4wMTE2OUw3LjY2NzM0IDYuMTIzNTVDNy44ODkxMSA2LjM0NDk1IDggNi42MDM1OCA4IDYuODk5NDVDOCA3LjE5NDgxIDcuODg5MTEgNy40NDgxMiA3LjY2NzM0IDcuNjU5MzhDNy40NTU3MSA3Ljg4MDc3IDcuMTk5MTggNy45OTE0NyA2Ljg5NzczIDcuOTkxNDdDNi41OTYyOCA3Ljk5MTQ3IDYuMzM5OTkgNy44ODA3NyA2LjEyODg4IDcuNjU5MzhMNC4wMTIyNiA1LjU2MzQ4WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==")`,
    },
    "&:focus:checked + span": css({
      outline: "0",
      borderRadius: "34px",
      backgroundPositionY: "5px",
      backgroundPositionX: "6px",
      backgroundRepeat: "no-repeat",
      backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEwIDgiIGZpbGw9IiNmZmZmZmYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik05LjY3NDIzIDAuMzM0NjM0QzkuODgyODIgMC41NDg5NjQgMTAgMC44Mzk2MiAxMCAxLjE0MjY4QzEwIDEuNDQ1NzUgOS44ODI4MiAxLjczNjQgOS42NzQyMyAxLjk1MDczTDQuMTEwOTUgNy42NjUzN0MzLjkwMjI5IDcuODc5NjMgMy42MTkzMyA4IDMuMzI0MyA4QzMuMDI5MjYgOCAyLjc0NjMgNy44Nzk2MyAyLjUzNzY1IDcuNjY1MzdMMC4zMTIzMzYgNS4zNzk1MUMwLjEwOTY1NiA1LjE2Mzk1IC0wLjAwMjQ5MzA0IDQuODc1MjUgNC4yMDYxNGUtMDUgNC41NzU1OEMwLjAwMjU3NzE2IDQuMjc1OTEgMC4xMTk1OTQgMy45ODkyNCAwLjMyNTg5IDMuNzc3MzRDMC41MzIxODUgMy41NjU0MyAwLjgxMTI1MyAzLjQ0NTIzIDEuMTAyOTkgMy40NDI2MkMxLjM5NDcyIDMuNDQwMDIgMS42NzU3OCAzLjU1NTIyIDEuODg1NjMgMy43NjM0MUwzLjMyNDMgNS4yNDEyMkw4LjEwMDkzIDAuMzM0NjM0QzguMzA5NTkgMC4xMjAzNjggOC41OTI1NCAwIDguODg3NTggMEM5LjE4MjYyIDAgOS40NjU1OCAwLjEyMDM2OCA5LjY3NDIzIDAuMzM0NjM0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==")`,
    }),
    "&:focus:not(:checked) + span": css({
      outline: "0",
      borderRadius: "34px",
      backgroundPositionY: "5px",
      backgroundPositionX: "22px",
      backgroundRepeat: "no-repeat",
      backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgOCA4IiBmaWxsPSIjZmZmZmZmIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cGF0aCBkPSJNNC4wMTIyNiA1LjU2MzQ4TDEuODk1NjUgNy42NzUzNEMxLjY3Mzg4IDcuODk2NzMgMS40MTQ4IDguMDA0OSAxLjExODQzIDcuOTk5ODNDMC44MjI1NjEgNy45OTQyNiAwLjU2MzQ4NyA3Ljg4MDc3IDAuMzQxMjA1IDcuNjU5MzhDMC4xMTk0MzEgNy40Mzc0OCAwLjAwODU0Mzg3IDcuMTc4ODUgMC4wMDg1NDM4NyA2Ljg4MzQ5QzAuMDA4NTQzODcgNi41ODc2MiAwLjExOTQzMSA2LjMyODk5IDAuMzQxMjA1IDYuMTA3NTlMMi40NDE4MyAzLjk5NTc0TDAuMzI1MjE5IDEuODgzODhDMC4xMDM0NDUgMS42NjI0OCAtMC4wMDQ5MDQ2MiAxLjQwNDExIDAuMDAwMTcwMyAxLjEwODc0QzAuMDA1NzUyNzEgMC44MTI4NzcgMC4xMTk0MzEgMC41NTQyNDUgMC4zNDEyMDUgMC4zMzI4NTFDMC41NTI4MjkgMC4xMTA5NSAwLjgwOTM2NiAwIDEuMTEwODIgMEMxLjQxMjI3IDAgMS42NzM4OCAwLjExMDk1IDEuODk1NjUgMC4zMzI4NTFMMy45OTYyOCAyLjQ0NDcxTDYuMDk2OSAwLjMzMjg1MUM2LjMxODY4IDAuMTEwOTUgNi41ODAyOSAwIDYuODgxNzQgMEM3LjE4MzE5IDAgNy40NDUwNiAwLjExMDk1IDcuNjY3MzQgMC4zMzI4NTFDNy44ODkxMSAwLjU1NDI0NSA4IDAuODE1NDEgOCAxLjExNjM0QzggMS40MTcyOCA3Ljg4OTExIDEuNjc4NDQgNy42NjczNCAxLjg5OTg0TDUuNTUwNzMgNC4wMTE2OUw3LjY2NzM0IDYuMTIzNTVDNy44ODkxMSA2LjM0NDk1IDggNi42MDM1OCA4IDYuODk5NDVDOCA3LjE5NDgxIDcuODg5MTEgNy40NDgxMiA3LjY2NzM0IDcuNjU5MzhDNy40NTU3MSA3Ljg4MDc3IDcuMTk5MTggNy45OTE0NyA2Ljg5NzczIDcuOTkxNDdDNi41OTYyOCA3Ljk5MTQ3IDYuMzM5OTkgNy44ODA3NyA2LjEyODg4IDcuNjU5MzhMNC4wMTIyNiA1LjU2MzQ4WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==")`,
    }),
  }),
  slider: css({
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: "34px",
    "&:before": {
      position: "absolute",
      content: "''",
      height: "14px",
      width: "14px",
      left: "2px",
      bottom: "2px",
      transition: "0.2s",
      borderRadius: "50%",
    },
  }),
};
